import React from "react";
import { merge, $ } from "glamor";
import { Button as UiButton } from "@material-ui/core";
import Button from "components/form/button";
import { makeStyles } from "@material-ui/core/styles";
import CalendarTodayOutlinedIcon from "@material-ui/icons/CalendarTodayOutlined";
import Badge from "@material-ui/core/Badge";
import { themeOptions } from "styles/theme";
import CartIcon from "components/icons/cart-outlined";
import { format } from "lib/globalize-util";
import { IOrder } from "records/order";
import { getHasShipDates } from "./build-order-utils";
import { DATATEST } from "constants/integration";
import { BuildOrderActions } from "constants/analytics";

interface IBuildOrderEntryProps {
    order: IOrder;
    onOpenBuildOrder: () => void;
}

const useStyles = makeStyles({
    label: {
        color: themeOptions.palette.primary1Color,
        paddingRight: "3px",
        paddingLeft: "3px",
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        verticalAlign: "middle",
    },
    buttonRoot: {
        padding: 0,
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        height: "100%",
        lineHeight: "40px",
        minWidth: "44px",
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        fontSize: 14,
    },
    calendarIcon: {
        fill: themeOptions.palette.darkGrey,
        width: 22,
        height: 22,
        marginLeft: 12,
        float: "left",
    },
    cartButton: { margin: "10px 10px 0 10px !important" },
});

export const BuildOrderEntry: React.FC<IBuildOrderEntryProps> = ({ order, onOpenBuildOrder }) => {
    let buildOrderButton = null;
    const classes = useStyles();

    const styles = React.useMemo(() => {
        return merge(
            $(" .base", {
                display: "flex",
                flexDirection: "row",
            }),
            $(" .action", {
                position: "relative",
                marginLeft: 0,
                marginRight: 8,
                paddingTop: 10,
                paddingBottom: 10,
            }),
            $(" .labelContainer", { display: "flex", flexDirection: "row", flexWrap: "nowrap" }),
            $(" .badgeContainer", { position: "relative", display: "inline-block", padding: 11, margin: 8 }),
            $(" .badge", {
                display: "flex",
                flexFlow: "row nowrap",
                justifyContent: "center",
                alignItems: "center",
                position: "absolute",
                top: 0,
                right: 0,
                fontWeight: 500,
                fontSize: 12,
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "rgb(47, 199, 190)",
                color: "white",
                float: "left",
            }),
        );
    }, []);

    const ship_dates = order?.__build_order.ship_dates ?? [];
    const hasShipDates = getHasShipDates({ order });

    const handleEntryBtn = React.useCallback(
        (e) => {
            const isCalendar = e.currentTarget.getAttribute("data-value") === "isCalendar";
            if (isCalendar) {
                global.analytics.track(BuildOrderActions.CalendarBtn);
            } else {
                global.analytics.track(BuildOrderActions.OrderSetupBtn);
            }

            onOpenBuildOrder();
        },
        [onOpenBuildOrder],
    );

    if (hasShipDates) {
        buildOrderButton = (
            <div className="action" data-test={DATATEST.ORDERS.BUILD_ORDER.CONTAINER}>
                <UiButton
                    onClick={handleEntryBtn}
                    data-value="isCalendar"
                    data-test={DATATEST.ORDERS.BUILD_ORDER.CALENDAR_BUTTON}
                    classes={{ label: classes.label, root: classes.buttonRoot }}
                    startIcon={<CalendarTodayOutlinedIcon classes={{ root: classes.calendarIcon }} />}
                >
                    <span className="labelContainer" data-test={DATATEST.ORDERS.BUILD_ORDER.COUNT}>
                        <div className="badgeContainer">
                            <Badge className="badge">{ship_dates.length}</Badge>
                        </div>
                    </span>
                </UiButton>
            </div>
        );
    } else {
        buildOrderButton = (
            <div className="cart-button base" data-test={DATATEST.ORDERS.BUILD_ORDER.CONTAINER}>
                <Button
                    onClick={handleEntryBtn}
                    data-test={DATATEST.ORDERS.BUILD_ORDER.ORDER_SETUP_BUTTON}
                    className={classes.cartButton}
                    labelStyle={{ padding: "0 20px 0 5px" }}
                    labelPosition="after"
                    primary={true}
                    icon={<CartIcon color={themeOptions.palette.canvasColor} />}
                    label={format("Order Setup")}
                />
            </div>
        );
    }

    return <div {...styles}>{buildOrderButton}</div>;
};
